import React, { useState, useEffect } from "react";
import MainLayout from "../components/SubscriptionLayout";
import { useAuth } from "../middleware/AuthContext";
import { fetchBusinessSolutionNames, fetchSolutionDataByName } from "../apis/fetchBusinessSolutionApi";
import { subscribeUserEmailforList, unSubscribeUserEmailforList, fetchActiveSubsriptionForUserEmail} from "../apis/fetchSubscriptionStatusApi";
import { ShowCustomAlert } from "../components/showCustomAlert";
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Box,
    Typography,
} from "@mui/material";

const AnnouncementSubscr: React.FC = () => {
    // Separate states for subscribe and unsubscribe sections
    const [subscribeItems, setSubscribeItems] = useState<Record<string, boolean>>({});
    const [unsubscribeItems, setUnsubscribeItems] = useState<Record<string, boolean>>({});
    const [activeSubscriptions, setActiveSubscriptions] = useState<{ id: string; label: string }[]>([]);
    const [groupedSubscriptions, setGroupedSubscriptions] = useState<GroupedSubscriptions>({
        majorIncidents: [],
        changes: [],
    }); // State for grouped subscriptions
    const [newGroupedSubscriptions, setNewGroupedSubscriptions] = useState<GroupedSubscriptions>({
        majorIncidents: [],
        changes: [],
    }); // State for grouped subscriptions

    // Define the type for grouped subscriptions
    type GroupedSubscriptions = {
        majorIncidents: { id: string; label: string }[];
        changes: { id: string; label: string }[];
    };

    const handleSubscribeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscribeItems({
            ...subscribeItems,
            [event.target.name]: event.target.checked,
        });
    };

    const handleUnsubscribeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnsubscribeItems({
            ...unsubscribeItems,
            [event.target.name]: event.target.checked,
        });
    };

    const [, setNameToLabelMap] = useState<Record<string, string>>({});
    const { userEmail, csrfToken } = useAuth();

    const fetchData = React.useCallback(async () => {
        if (!userEmail) {
            console.warn("User email is missing.");
            return;
        }

        try {
            const majorIncidentListNames = await fetchBusinessSolutionNames("majorIncident");
            const changeListNames = await fetchBusinessSolutionNames("change");

            // Grouping the lists
            const newGroupedSubscriptions: GroupedSubscriptions = {
                majorIncidents: [],
                changes: [],
            };
            
            // For active subscriptions
            const activeGroupedSubscriptions: GroupedSubscriptions = {
                majorIncidents: [],
                changes: [],
            };

            const allListNames = [...majorIncidentListNames, ...changeListNames];

            const newSubscribeItems: Record<string, boolean> = {};
            const newUnsubscribeItems: Record<string, boolean> = {};
            const labelMap: Record<string, string> = {};
            const activeSubs: { id: string, label: string }[] = [];

            const activeListIds = await fetchActiveSubsriptionForUserEmail();

            const infoResponses = await Promise.all(
                allListNames.map(async (businessName) => {
                    const data = await fetchSolutionDataByName(businessName, csrfToken);
                    if (!data || !data.id) return null;

                    return {
                        id: data.id,
                        label: data.name || businessName,
                        type: majorIncidentListNames.includes(businessName) ? "majorIncident" : "change",
                        isActive: activeListIds.includes(data.id),
                    };
                })
            );

            for (const info of infoResponses) {
                if (!info) continue;

                const { id, label, type, isActive } = info;

                labelMap[id] = label;

                if (isActive) {
                    newUnsubscribeItems[id] = false;
                    activeSubs.push({ id, label });

                    if (type === "majorIncident") {
                        activeGroupedSubscriptions.majorIncidents.push({ id, label });
                    } else {
                        activeGroupedSubscriptions.changes.push({ id, label });
                    }
                } else {
                    newSubscribeItems[id] = false;
                    if (type === "majorIncident") {
                        newGroupedSubscriptions.majorIncidents.push({ id, label });
                    } else {
                        newGroupedSubscriptions.changes.push({ id, label });
                    }
                }
            }

            setActiveSubscriptions(activeSubs);
            setNameToLabelMap(labelMap);
            setSubscribeItems(newSubscribeItems);
            setUnsubscribeItems(newUnsubscribeItems);
            setGroupedSubscriptions(activeGroupedSubscriptions); // Now contains active subscriptions
            setNewGroupedSubscriptions(newGroupedSubscriptions); // Contains available subscriptions
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [csrfToken, userEmail]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubscribe = async () => {
        try {
            const selected = Object.entries(subscribeItems)
                .filter(([_, checked]) => checked)
                .map(([name]) => name);

            for (const listName of selected) {
                await subscribeUserEmailforList(listName, csrfToken);
            }

            ShowCustomAlert("Successfully subscribed to selected lists.");
            await fetchData(); // Update lists
        } catch (error) {
            console.error("Subscription error:", error);
            ShowCustomAlert("Subscription failed.");
        }
    };

    const handleUnsubscribe = async () => {
        try {
            const selected = Object.entries(unsubscribeItems)
                .filter(([_, checked]) => checked)
                .map(([name]) => name);

            for (const listName of selected) {
                await unSubscribeUserEmailforList(listName, csrfToken);
            }

            ShowCustomAlert("Successfully unsubscribed from selected lists.");
            await fetchData(); // Update lists
        } catch (error) {
            console.error("Unsubscription error:", error);
            ShowCustomAlert("Unsubscription failed.");
        }
    };

    const formatLabelForDisplay = (label: string, id: string) => {
        return label.includes('(') ? label : `${label} (${id})`;
    };

    return (
        <MainLayout>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}>
                Manage your subscriptions
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 3, textAlign: "center", color: "gray" }}>
                Managing subscriptions for the logged-in email: {userEmail}
            </Typography>

            {/* Active Subscriptions Section */}
            <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                    Active Subscriptions:
                </Typography>
                {activeSubscriptions.length > 0 ? (
                    <>
                        {groupedSubscriptions.majorIncidents.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}>
                                    Major Incidents Announcement
                                </Typography>
                                <ul style={{ listStyle: "none", padding: 0 }}>
                                    {groupedSubscriptions.majorIncidents.map(({ id, label }) => (
                                        <li key={id} style={{ display: "flex", justifyContent: "center", marginBottom: "8px" }}>
                                            <span style={{ marginRight: "10px" }}>✅</span>
                                            <span>{formatLabelForDisplay(label, id)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {groupedSubscriptions.changes.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, textAlign: "center"  }}>
                                    Change Announcement
                                </Typography>
                                <ul style={{ listStyle: "none", padding: 0 }}>
                                    {groupedSubscriptions.changes.map(({ id, label }) => (
                                        <li key={id} style={{ display: "flex", justifyContent: "center", marginBottom: "8px" }}>
                                            <span style={{ marginRight: "10px" }}>✅</span>
                                            <span>{formatLabelForDisplay(label, id)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </>
                ) : (
                    <Typography variant="body1" sx={{ textAlign: "center", color: "gray" }}>
                        No active subscriptions
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, margin: "auto" }}>
                {/* Subscribe section */}
                <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, flex: 1, minWidth: '45%' }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                        Subscribe to announcements:
                    </Typography>
                    {Object.keys(subscribeItems).length > 0 ? (
                        <>
                            {newGroupedSubscriptions.majorIncidents.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                                        Major Incidents Announcements
                                    </Typography>
                                    <FormGroup sx={{ mb: 2, pl:2 }}>
                                        {newGroupedSubscriptions.majorIncidents.map(({ id, label }) => (
                                            <FormControlLabel
                                                key={id}
                                                control={
                                                    <Checkbox
                                                        checked={subscribeItems[id] || false}
                                                        onChange={handleSubscribeChange}
                                                        name={id}
                                                    />
                                                }
                                                label={formatLabelForDisplay(label, id)}
                                            />
                                        ))}
                                    </FormGroup>
                                </>
                            )}
                            {newGroupedSubscriptions.changes.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1}}>
                                        Change Announcements
                                    </Typography>
                                    <FormGroup sx={{ mb: 2, pl: 2 }}>
                                        {newGroupedSubscriptions.changes.map(({ id, label }) => (
                                            <FormControlLabel
                                                key={id}
                                                control={
                                                    <Checkbox
                                                        checked={subscribeItems[id] || false}
                                                        onChange={handleSubscribeChange}
                                                        name={id}
                                                    />
                                                }
                                                label={formatLabelForDisplay(label, id)}
                                            />
                                        ))}
                                    </FormGroup>
                                </>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mb: 3 }}
                                onClick={handleSubscribe}
                                disabled={Object.values(subscribeItems).every((checked) => !checked)} // Disable if no items are selected
                            >
                                Subscribe
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ textAlign: "center", color: "gray" }}>
                            No subscription lists to subscribe
                        </Typography>
                    )}
                </Box>

                {/* Unsubscribe section */}
                <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, flex: 1, minWidth: '45%' }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                        Unsubscribe from announcements:
                    </Typography>
                    {Object.keys(unsubscribeItems).length > 0 ? (
                        <>
                            {groupedSubscriptions.majorIncidents.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1,  }}>
                                        Major Incidents Announcement
                                    </Typography>
                                    <FormGroup sx={{ mb: 2, pl:2 }}>
                                        {groupedSubscriptions.majorIncidents.map(({ id, label }) => (
                                            <FormControlLabel
                                                key={id}
                                                control={
                                                    <Checkbox
                                                        checked={unsubscribeItems[id] || false}
                                                        onChange={handleUnsubscribeChange}
                                                        name={id}
                                                    />
                                                }
                                                label={formatLabelForDisplay(label, id)}
                                            />
                                        ))}
                                    </FormGroup>
                                </>
                            )}
                            {groupedSubscriptions.changes.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1,  }}>
                                        Change Announcement
                                    </Typography>
                                    <FormGroup sx={{ mb: 2, pl: 2}}>
                                        {groupedSubscriptions.changes.map(({ id, label }) => (
                                            <FormControlLabel
                                                key={id}
                                                control={
                                                    <Checkbox
                                                        checked={unsubscribeItems[id] || false}
                                                        onChange={handleUnsubscribeChange}
                                                        name={id}
                                                    />
                                                }
                                                label={formatLabelForDisplay(label, id)}
                                            />
                                        ))}
                                    </FormGroup>
                                </>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mb: 3 }}
                                onClick={handleUnsubscribe}
                                disabled={Object.values(unsubscribeItems).every((checked) => !checked)} // Disable if no items are selected
                            >
                                Unsubscribe
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ textAlign: "center", color: "gray" }}>
                            No subscription lists to unsubscribe
                        </Typography>
                    )}
                </Box>
            </Box>
        </MainLayout>
    );
};

export default AnnouncementSubscr;

